import { ArrowLeft, ExternalLink, FileSearch, Headphones, Home } from 'lucide-react';
import { Link, Navigate, createBrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { VendorLifecycleListPage } from '@/features/vendors/VendorLifecycleListPage';
import { VendorLifecycleDetailPage } from '@/features/vendors/VendorLifecycleDetailPage';
import { MyApprovalsPage } from '@/features/workflow/MyApprovalsIntegratedPage';
import { WorkflowDefinitionEditorPage, WorkflowDefinitionListPage } from '@/features/workflow/WorkflowIntegratedPages';
import { AuditLogPage } from '@/features/admin/AuditLogPage';
import { LoginPage } from '@/features/portal/PortalPages';
import { PortalOnboardingStatusPage, PortalSubmittedDetailsPage } from '@/features/portal/VendorPortalPages';
import { VendorChangePasswordPage } from '@/features/portal/VendorChangePasswordPage';
import { VendorInvitesPage } from '@/features/vendors/VendorInvitesIntegratedPage';
import { VendorRegistrationPage } from '@/features/portal/VendorRegistrationPage';
import { RequireAnyPermission, RequireAuth, RequirePasswordChanged, RequirePermission } from '@/auth/RouteGuards';
import { ProcessFlowPage } from '@/features/process-flow/ProcessFlowPage';
import { NotificationsPage } from '@/features/admin/NotificationsPage';
import { IamPage } from '@/features/admin/IamPage';
import { MasterDataManagementPage } from '@/features/admin/MasterDataManagementPage';
import { AttachmentsPage } from '@/features/admin/AttachmentsPage';
import { InternalPurchaseOrderDetailPage, InternalPurchaseOrdersPage, VendorPurchaseOrderDetailPage, VendorPurchaseOrdersPage } from '@/features/purchase-orders/PurchaseOrderPages';
import { GoodsReceiptDetailPage, GoodsReceiptsPage } from '@/features/goods-receipts/GoodsReceiptPages';
import { InternalVendorInvoiceDetailPage, InternalVendorInvoicesPage, PortalVendorInvoiceDetailPage } from '@/features/invoices/VendorInvoicePages';
import { FinanceAdvancesPage, PaymentDetailPage, PaymentsPage } from '@/features/finance/FinancePages';
import { ResetPasswordPage } from '@/features/auth/PasswordRecoveryPages';
import { VendorDashboardPage, VendorGoodsReceiptsPage, VendorHelpPage, VendorInvoicesPage, VendorProfilePage } from '@/features/portal/VendorWorkspacePages';
import { useAuth } from '@/auth/AuthProvider';

function NotFound() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const destination = user?.userType === 'VENDOR' ? '/portal' : user ? '/app' : location.pathname.startsWith('/portal') ? '/portal/login' : '/login';
  const destinationLabel = user ? 'Go to overview' : 'Go to sign in';

  return <div className="not-found"><main className="not-found__card">
    <span className="not-found__icon"><FileSearch /></span><span className="not-found__code">404</span><h1>Page not found</h1><p>The page you’re looking for doesn’t exist, has been moved,<br />or you don’t have permission to access it.</p>
    <div className="not-found__actions"><Link className="not-found__action" to={destination}><span><Home /></span><div><strong>{destinationLabel}</strong><small>Return to your main workspace</small></div></Link><button className="not-found__action" onClick={() => navigate(-1)}><span><ArrowLeft /></span><div><strong>Go back</strong><small>Return to the previous page</small></div></button></div>
    <div className="not-found__help-title"><span>Need help?</span></div>
    <div className="not-found__support"><span className="not-found__support-icon"><Headphones /></span><div><strong>Still need help?</strong><small>Email vendizsupport@gmail.com for assistance.</small></div><a href="mailto:vendizsupport@gmail.com">Contact support <ExternalLink /></a></div>
  </main></div>;
}

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/login" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/portal/login', element: <LoginPage portal /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/register', element: <VendorRegistrationPage /> },
  { path: '/register/:token', element: <VendorRegistrationPage /> },
  { path: '/vendor-registration/:token', element: <VendorRegistrationPage /> },
  { element: <RequireAuth userType="INTERNAL" />, children: [
    { path: '/app', element: <AppLayout />, children: [
      { index: true, element: <DashboardPage /> },
      { path: 'approvals', element: <MyApprovalsPage /> },
      { path: 'my-approvals', element: <MyApprovalsPage /> },
      { path: 'process-flow', element: <ProcessFlowPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'attachments', element: <AttachmentsPage /> },
      { path: 'purchase-orders', element: <InternalPurchaseOrdersPage /> },
      { path: 'purchase-orders/:id', element: <InternalPurchaseOrderDetailPage /> },
      { path: 'goods-receipts', element: <GoodsReceiptsPage /> },
      { path: 'goods-receipts/:id', element: <GoodsReceiptDetailPage /> },
      { path: 'invoices', element: <InternalVendorInvoicesPage /> },
      { path: 'invoices/:id', element: <InternalVendorInvoiceDetailPage /> },
      { element: <RequirePermission permission="PAYMENT.VIEW" />, children: [
        { path: 'finance/advances', element: <FinanceAdvancesPage /> },
        { path: 'payments', element: <PaymentsPage /> },
        { path: 'payments/:id', element: <PaymentDetailPage /> },
      ] },
      { element: <RequireAnyPermission permissions={['VENDOR.VIEW', 'VENDOR.CREATE', 'VENDOR.APPROVE']} />, children: [
        { path: 'vendors', element: <VendorLifecycleListPage /> },
        { path: 'vendors/:id', element: <VendorLifecycleDetailPage /> },
      ] },
      { element: <RequirePermission permission="VENDOR.CREATE" />, children: [{ path: 'vendor-invites', element: <VendorInvitesPage /> }] },
      { element: <RequirePermission permission="WORKFLOW.CONFIGURE" />, children: [
        { path: 'workflows', element: <WorkflowDefinitionListPage /> },
        { path: 'workflows/:id', element: <WorkflowDefinitionEditorPage /> },
      ] },
      { element: <RequirePermission permission="USER.MANAGE" />, children: [
        { path: 'users', element: <IamPage /> },
        { path: 'settings/master-data', element: <MasterDataManagementPage /> },
        { path: 'settings/company', element: <MasterDataManagementPage initialTab="companies" /> },
        { path: 'audit', element: <AuditLogPage /> },
      ] },
    ] },
  ] },
  { element: <RequireAuth userType="VENDOR" />, children: [
    { path: '/portal/change-password', element: <VendorChangePasswordPage /> },
    { element: <RequirePasswordChanged />, children: [
      { path: '/portal', element: <PortalLayout />, children: [
        { index: true, element: <VendorDashboardPage /> },
        { path: 'profile', element: <VendorProfilePage /> },
        { path: 'onboarding', element: <PortalOnboardingStatusPage /> },
        { path: 'onboarding/details', element: <PortalSubmittedDetailsPage /> },
        { element: <RequirePermission permission="PORTAL.PO.VIEW" />, children: [
          { path: 'purchase-orders', element: <VendorPurchaseOrdersPage /> },
          { path: 'purchase-orders/:id', element: <VendorPurchaseOrderDetailPage /> },
        ] },
        { path: 'goods-receipts', element: <VendorGoodsReceiptsPage /> },
        { path: 'goods-receipts/:id', element: <GoodsReceiptDetailPage portal /> },
        { element: <RequirePermission permission="PORTAL.INVOICE.UPLOAD" />, children: [
          { path: 'invoices', element: <VendorInvoicesPage /> },
          { path: 'invoices/:id', element: <PortalVendorInvoiceDetailPage /> },
        ] },
        { element: <RequirePermission permission="PORTAL.PAYMENT.VIEW" />, children: [
          { path: 'payments', element: <PaymentsPage portal /> },
          { path: 'payments/:id', element: <PaymentDetailPage portal /> },
        ] },
        { path: 'notifications', element: <NotificationsPage /> },
        { path: 'help', element: <VendorHelpPage /> },
      ] },
    ] },
  ] },
  { path: '*', element: <NotFound /> },
]);
